import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getFrequencyLabel } from "@/features/office/places/detail/_libs/list-item";
import { ListItemFrequency } from "@/types/openapi-enum";
import { Typography } from "../ui/typography";

type FrequencySelectProps = {
  className?: string;
  value: ListItemFrequency;
  onChange: (value: ListItemFrequency) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
};

const frequencyOptions = [
  ListItemFrequency.Once,
  ListItemFrequency.BiWeekly,
  ListItemFrequency.Weekly,
  ListItemFrequency.Monthly,
  ListItemFrequency.Quarterly,
  ListItemFrequency.BiAnnual,
  ListItemFrequency.Annual,
];

export default function FrequencySelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: FrequencySelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex items-center justify-between",
            "w-full min-h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg outline outline-outline-dark-low",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            !value && "text-text-dark-min",
          )}
          {...props}
        >
          {value ? (
            <Typography
              className="flex items-center gap-2 text-text-dark"
              size="sm"
            >
              {getFrequencyLabel(value)}
            </Typography>
          ) : (
            "Select frequency"
          )}

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false} tabIndex={0}>
          <CommandList className="outline-none">
            <CommandEmpty>No frequency found.</CommandEmpty>
            <CommandGroup>
              {frequencyOptions.map((frequency: any) => (
                <CommandItem
                  value={frequency}
                  key={frequency}
                  onSelect={() => {
                    // Set value to form field of the parent
                    onChange(frequency);
                    handleOpenChange(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <Typography
                      className="flex items-center gap-2 text-text-dark"
                      size="sm"
                    >
                      {getFrequencyLabel(frequency)}
                    </Typography>

                    <BwCheckmarkLine
                      size={16}
                      className={cn(
                        /* sorted */
                        "shrink-0",
                        value === frequency ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
